import { ReactElement, useMemo } from 'react';

import { FormControl, FormField, FormItem, FormMessage } from '@crossnokaye/ux-common';

import { SettingFieldProps } from '@/features/equipment-browser/equipment-details/settings/components/setting-field.types';
import { SettingFieldLabelRow } from '@/features/equipment-browser/equipment-details/settings/components/setting-field-label-row';
import { useFieldIsDirty } from '@/features/equipment-browser/equipment-details/settings/components/use-field-is-dirty';
import { getSourceUnits } from '@/features/equipment-browser/utils';
import { getCatalogUnitsLabel } from '@/helpers/unit-formatter/get-catalog-units-label';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import { Input, InputField, InputUnit, InputValueChange } from '@/ux-common/components/input/input';
import { withTooltip } from '@/ux-common/components/tooltip/with-tooltip';

const InputFieldWithTooltip = withTooltip(InputField);

/**
 * Input field component for text/number settings.
 * Shows change indicator when value differs from original.
 */
export const SettingFieldInput = ({ form, name, setting }: SettingFieldProps): ReactElement => {
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    privilege: PRIVILEGES.writeCommands,
  });

  const sourceLabels = useMemo(() => {
    // If setting.units is not configured, assume the value is already in displayUnits
    const sourceUnits = getSourceUnits(setting);
    return formatUnits({ value: setting?.value, units: sourceUnits }, setting?.displayUnits);
  }, [setting]);

  const hasChanged = useFieldIsDirty(form.control, name);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        // Typed values are always in displayUnits. Vacuum formatting converts
        // the stored value to INHG for display; once the field is dirty the
        // label switches to the catalog displayUnits label.
        const unitsLabel = sourceLabels.unitsLabel?.toUpperCase();
        const units = hasChanged ? getCatalogUnitsLabel(setting.displayUnits) : unitsLabel;

        return (
          <FormItem data-component="SettingFieldInput">
            {/* `new`, not `dirty`: a setpoint is a control value where the previous
                one matters, so the field shows the `← oldValue` lockup as well as
                the staged border, and highlights the new value and its unit. That
                replaces the hand-rolled yellow spans and the ArrowBigLeft. */}
            <Input state={hasChanged ? 'new' : undefined}>
              <SettingFieldLabelRow description={setting.description} label={setting.label} />
              <FormControl>
                <InputFieldWithTooltip
                  // `FormControl` always emits `aria-describedby`, pointing at
                  // `formDescriptionId` — and no `FormDescription` is rendered here
                  // or anywhere in gaia, so it resolves to nothing. Passing
                  // undefined lets the group supply its own: the refusal reason and
                  // any `InputHelper`, which are real. Child props win over the
                  // Slot's.
                  aria-describedby={undefined}
                  // Refused, not disabled: a natively disabled input drops out of
                  // the tab order, so a keyboard user never reached it and never
                  // heard why — the same gap the two Select fields had. readOnly
                  // underneath blocks typing, paste and drop while keeping the
                  // value selectable, which matters for a setpoint you may want to
                  // read but cannot change.
                  disabledReason={disabledByPermissions ? tooltipText : undefined}
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  tooltipArrow
                  tooltipContent={disabledByPermissions ? tooltipText : undefined}
                  value={field.value}
                >
                  {units && <InputUnit>{units}</InputUnit>}
                  {hasChanged && sourceLabels.valueLabel && (
                    <InputValueChange
                      oldValue={sourceLabels.valueLabel}
                      unit={unitsLabel || undefined}
                    />
                  )}
                </InputFieldWithTooltip>
              </FormControl>
            </Input>
            <FormMessage className={'pl-2'} />
          </FormItem>
        );
      }}
    />
  );
};
